/**
 * Recital Master design tokens.
 *
 * Direction: premium, near-black, cool-neutral canvas — a quiet stage that
 * lets each year's recital artwork carry the personality. The brand accent
 * is the app icon's pastel gradient (cyan → blue → violet → pink), used
 * sparingly: primary actions, playback highlights, the brand mark. Pink is
 * reserved for My Dancer moments. Depth comes from imagery — sharp artwork
 * over its own blurred backdrop — not from borders and boxes.
 */

export const palette = {
  // Canvas
  bg: "#0D0E14",
  bgDeep: "#08090E",
  surface: "#14161F",
  surfaceRaised: "#1B1E2B",
  surfaceSunken: "#10121A",
  /** Near-opaque floating chrome above artwork and route content. */
  // Fully opaque: floating surfaces sit over scrolling text, and translucency
  // without backdrop blur reads as bleed-through, not depth.
  surfaceOverlay: "rgb(22, 24, 34)",

  // Cool translucent hairlines
  hairline: "rgba(167, 178, 216, 0.12)",
  hairlineStrong: "rgba(167, 178, 216, 0.24)",

  // Cool translucent surface washes
  coolWashFaint: "rgba(167, 178, 216, 0.05)",
  coolWashSubtle: "rgba(167, 178, 216, 0.06)",
  coolWash: "rgba(167, 178, 216, 0.08)",
  coolWashStrong: "rgba(167, 178, 216, 0.10)",
  coolWashHover: "rgba(167, 178, 216, 0.14)",

  // Ink
  text: "#F3F4FA",
  textSoft: "#C9CDDE",
  muted: "#9CA2BC",
  faint: "#646B87",

  // Brand accents (pastel, from the icon)
  cyan: "#79D2F4",
  blue: "#85A3F8",
  violet: "#B49AF6",
  pink: "#F2A0CC",
  pinkDeep: "#D9709F",
  /** Solid accent for links, focus, "now playing". */
  accent: "#8FA8FA",
  /** Dark ink used on pastel gradient / pastel fills. */
  inkOnAccent: "#121426",

  danger: "#F28B82",
  /** Positive completion / engagement — deliberately mint rather than brand
      blue so success reads as an outcome, not an informational highlight. */
  success: "#8ED8B4",
  /** Attention without alarm — an access window running out, not a failure.
      Sits beside `danger` at the same softness so status pills read as one
      family as they warm from accent → warning → danger. */
  warning: "#F2C68A",

  // Player chrome
  playerBg: "#000000",
  scrim: "rgba(8, 9, 14, 0.72)",
  /** Dark glass for chips/pills floating over video or artwork — the one
      shell shared by the audio-feed chip, route pill, and their kin.
      (Mirrored in player-skin.css for the Video.js feedback islands.) */
  playerGlass: "rgba(20, 22, 31, 0.5)",
  /** Hover lift for playerGlass: warm one step, never plunge dark. */
  playerGlassHover: "rgba(32, 35, 46, 0.62)"
} as const;

/**
 * Web-only glossy pass for playerGlass surfaces: the footage behind the
 * chip smears through frosted. Apply behind a Platform.OS === "web" gate —
 * native RN has no backdrop-filter, and its glass leans on opacity alone.
 * (Mirrored in player-skin.css for the Video.js feedback islands.)
 */
export const playerGlassBlur = {
  backdropFilter: "blur(16px) saturate(140%)",
  WebkitBackdropFilter: "blur(16px) saturate(140%)"
} as const;

/** Gradient stop sets — the brand language. */
export const gradients = {
  /** Primary action gradient (icon palette, pastel). */
  brand: ["#79D2F4", "#8B99F7", "#D49BE8"] as const,
  /** My Dancer gradient. */
  pink: ["#F2A0CC", "#E88BB4"] as const,
  /** Progress / playback fill. */
  playback: ["#79D2F4", "#A99BF8", "#F2A0CC"] as const
};

export const fonts = {
  display: "PlusJakartaSans_700Bold",
  displaySemi: "PlusJakartaSans_600SemiBold",
  body: "Inter_400Regular",
  bodyMedium: "Inter_500Medium",
  bodySemiBold: "Inter_600SemiBold"
} as const;

export const type = {
  hero: { fontFamily: fonts.display, fontSize: 34, lineHeight: 40, letterSpacing: -0.6 },
  display: { fontFamily: fonts.display, fontSize: 27, lineHeight: 33, letterSpacing: -0.4 },
  title: { fontFamily: fonts.displaySemi, fontSize: 20, lineHeight: 26, letterSpacing: -0.2 },
  danceTitle: { fontFamily: fonts.bodySemiBold, fontSize: 16, lineHeight: 22 },
  body: { fontFamily: fonts.body, fontSize: 15, lineHeight: 22 },
  bodyStrong: { fontFamily: fonts.bodySemiBold, fontSize: 15, lineHeight: 22 },
  small: { fontFamily: fonts.body, fontSize: 13, lineHeight: 18 },
  smallStrong: { fontFamily: fonts.bodySemiBold, fontSize: 13, lineHeight: 18 },
  overline: {
    fontFamily: fonts.bodySemiBold,
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 1.2,
    textTransform: "uppercase" as const
  }
} as const;

export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48
} as const;

export const radius = {
  sm: 8,
  md: 12,
  lg: 18,
  xl: 26,
  pill: 999
} as const;

/** Canonical recital-cover artwork geometry (1200 × 1600). */
export const RECITAL_COVER_ASPECT_RATIO = 3 / 4;

/** Max content column width on large screens. */
export const CONTENT_MAX_WIDTH = 1040;

/** Max content column width for pages that opt into the wide layout. */
export const WIDE_CONTENT_MAX_WIDTH = 1400;

/** Viewport width below which cards/heroes stack for phones. */
export const NARROW_BREAKPOINT = 640;

/**
 * Viewport width at which content gutters begin, and the floor the column
 * holds once they do. Gutters ramp in from zero here rather than appearing
 * all at once, so a resizing web window never jumps.
 */
export const CONTENT_GUTTER_START = 900;

/** Widest gutter before CONTENT_MAX_WIDTH takes over on its own. */
export const CONTENT_MAX_GUTTER = 72;

/**
 * The content column width for a viewport.
 *
 * CONTENT_MAX_WIDTH alone left tablet PORTRAIT full-bleed: iPad Pro 13" is
 * 1032pt portrait, eight points under the 1040 cap, so the clamp never bit and
 * the column ran edge to edge — while the same device in landscape (1376pt)
 * clamped to 1040 and got composed gutters. This closes that gap.
 *
 * Two deliberate constraints on how hard the gutter is allowed to bite:
 *
 * - Phones stay full-bleed. Gutters there only waste the narrow axis.
 * - The column never falls below CONTENT_GUTTER_START. An earlier version used
 *   a proportional gutter that pulled iPad Pro 11 / Air portrait (834pt) down
 *   to ~717pt, crossing MediaCard's 720pt tier boundary — those show cards
 *   demoted to the stacked `split` anatomy while a 13" showed the compact one.
 *   Holding the floor at 900 keeps every tablet on the same card anatomy.
 *
 * Net effect: only viewports above 900pt move, and landscape/desktop are
 * untouched because the 1040 cap still dominates there.
 */
export function contentMaxWidth(viewportWidth: number, wide = false): number {
  if (wide) return WIDE_CONTENT_MAX_WIDTH;
  if (viewportWidth <= CONTENT_GUTTER_START) return CONTENT_MAX_WIDTH;
  return Math.min(
    CONTENT_MAX_WIDTH,
    Math.max(CONTENT_GUTTER_START, viewportWidth - 2 * CONTENT_MAX_GUTTER)
  );
}

/**
 * The one focus/hover ring language (matches the inputs): softened accent
 * border + translucent glow. Spread into any interactive surface's
 * focused/hovered style. Web-only shadow; native relies on the border.
 * Keyboard focus (:focus-visible) is handled globally in design/global.css —
 * don't add per-component outlines for it.
 */
export const focusRing = {
  borderColor: "rgba(143, 168, 250, 0.55)",
  boxShadow: "0 0 0 3px rgba(143, 168, 250, 0.15)"
} as const;

/** Softer variant for hover (no keyboard intent). Hover on dark surfaces
 * should read as light — the backdrop already brightens — so the ring only
 * whispers the boundary. Keyboard focus stays assertive above. */
export const hoverRing = {
  borderColor: "rgba(167, 178, 216, 0.2)"
} as const;

/** Stronger hover boundary for controls whose resting border is transparent. */
export const hoverRingStrong = {
  borderColor: "rgba(167, 178, 216, 0.35)"
} as const;
